import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Transition } from "@headlessui/react";
import moment from "moment-timezone";
import { Download } from "lucide-react";
import { DateRange, DayPicker } from "react-day-picker";
import { Button, Input, Table } from "src/components";
import filterIcon from "src/assets/filterIcon.svg";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { toast } from "src/hooks/useToast";
import { downloadInvoice, fetchCategory3Files } from "src/services/invoice-api";

const DAY_PICKER_CLASSNAMES = {
  root: "border border-[#D0D0D0] rounded-xl p-5 flex items-center justify-center text-[1rem]",
  today: "border border-[#8051FF]",
  selected: "bg-[#ded9ff] border-[#8051FF] font-semibold",
  day: "p-2 cursor-pointer",
  chevron: "fill-[#7F56D9] hover:fill-[#8051FF] transition-colors",
  outside: "text-gray-400",
  nav: "flex items-start",
};

const DATE_TIME_FORMAT = "DD-MMM-YY HH:mm:ss";
// Default lookback window applied until the user picks a different range from the filter.
const DEFAULT_LOOKBACK_DAYS = 30;

const pickField = (row: any, keys: string[]) => {
  for (const key of keys) {
    if (row?.[key] !== "undefined" && row?.[key] !== null) return row[key];
  }
  return undefined;
};

// Maps a /cat3-list row to the fixed shape the table columns below expect.
// Sample row: { einvoice_attachment_id, stockist_id, medvol_code, stockist_name, city_name,
//               einvoice_attachment_path, uploaded_date, uploaded_by }
const normalizeCategory3Row = (row: any) => ({
  ph_code: pickField(row, ["medvol_code", "ph_code", "Ph_Code"]),
  stockist_name: pickField(row, ["stockist_name", "StockistName"]),
  city_name: pickField(row, ["city_name", "City_Name"]),
  uploaded_date: pickField(row, ["uploaded_date", "created_date", "Created_Date"]),
  // File key/path for the attachment — passed to the get-file API to resolve a downloadable URL.
  file_key: pickField(row, ["einvoice_attachment_path", "file_key", "file_path", "attachment_path"]),
});

// TODO: replace with the actual response shape once the API contract is finalized.
const getCategory3Columns = (onDownload: (row: any) => void) => [
  {
    id: "sno",
    header: "Sno",
    cell: (info: any) => info.row.index + 1,
  },
  {
    header: "Ph_Code",
    accessorKey: "ph_code",
  },
  {
    header: "StockistName",
    accessorKey: "stockist_name",
  },
  {
    header: "City_Name",
    accessorKey: "city_name",
  },
  {
    header: "Uploaded_Date",
    accessorKey: "uploaded_date",
    // API returns UTC timestamps (e.g. "2026-08-24T04:49:29.080Z") — convert to IST before display,
    // otherwise the time shown is off from what was actually uploaded.
    cell: (info: any) => (info.getValue() ? moment.utc(info.getValue()).tz("Asia/Kolkata").format(DATE_TIME_FORMAT) : "-"),
  },
  {
    id: "download",
    header: "Download File",
    cell: (info: any) => (
        <button
          type="button"
          aria-label="Download file"
          className="text-[#2FA84F] hover:opacity-70 disabled:opacity-40"
          onClick={() => onDownload(info.row.original)}
        >
          <Download size={18} />
        </button>
    ),
  },
];

const Category3Files = () => {
  const todayStr = moment().format("YYYY-MM-DD");
  const defaultFromDateStr = moment().subtract(DEFAULT_LOOKBACK_DAYS, "days").format("YYYY-MM-DD");

  const [searchValue, setSearchValue] = useState("");
  // Applied date range — drives the fetch. Defaults to the last DEFAULT_LOOKBACK_DAYS days.
  const [fromDate, setFromDate] = useState(defaultFromDateStr);
  const [toDate, setToDate] = useState(todayStr);
  // Draft range picked on the calendar inside the filter panel, committed to fromDate/toDate on Apply.
  const [draftRange, setDraftRange] = useState<DateRange | undefined>({
    from: moment(defaultFromDateStr).toDate(),
    to: moment(todayStr).toDate(),
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);

  const filterPanelRef = useOutsideClick(() => setIsFilterOpen(false));

  const hasActiveDateFilter = fromDate !== defaultFromDateStr || toDate !== todayStr;

  const handleOpenFilters = () => {
    // Re-sync the draft with whatever is currently applied, in case the panel
    // was previously closed without hitting Apply.
    setDraftRange({ from: moment(fromDate).toDate(), to: moment(toDate).toDate() });
    setIsFilterOpen(true);
  };

  const handleResetFilters = () => {
    setDraftRange({ from: moment(defaultFromDateStr).toDate(), to: moment(todayStr).toDate() });
    setFromDate(defaultFromDateStr);
    setToDate(todayStr);
  };

  const handleApplyFilters = () => {
    if (!draftRange?.from) {
      toast({ description: "Please select from and to date", variant: "destructive" });
      return;
    }

    // A single selected day comes back as { from, to: undefined } — treat it as a one-day range.
    const newFromDate = moment(draftRange.from).format("YYYY-MM-DD");
    const newToDate = moment(draftRange.to || draftRange.from).format("YYYY-MM-DD");

    setFromDate(newFromDate);
    setToDate(newToDate);
    setIsFilterOpen(false);
  };

  const fetchParams = useMemo(
    () => ({
      medvol_code: searchValue.trim(),
      from_date: fromDate,
      to_date: toDate,
    }),
    [searchValue, fromDate, toDate]
  );

  const fetchFiles = useCallback(async () => {
    if (!fetchParams.from_date || !fetchParams.to_date) return;

    setIsLoading(true);
    try {
      const res: any = await fetchCategory3Files(fetchParams);
      const rows = res?.data?.data ?? res?.data ?? [];
      setData((Array.isArray(rows) ? rows : []).map(normalizeCategory3Row));
    } catch (error) {
      console.error("Error fetching category 3 files:", error);
      toast({ description: "Failed to fetch category 3 files", variant: "destructive" });
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [fetchParams]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleDownload = async (row: any) => {
    if (!row?.file_key) {
      toast({ description: "File not available for download", variant: "destructive" });
      return;
    }

    try {
      const res: any = await downloadInvoice(row.file_key, 0);
      const url = res?.data?.data;
      if (res?.data?.success && url) {
        window.open(url, "_blank");
      } else {
        toast({ description: "Failed to download the file", variant: "destructive" });
      }
    } catch (error) {
      console.error("Error downloading category 3 file:", error);
      toast({ description: "Failed to download the file", variant: "destructive" });
    }
  };

  const columns = useMemo(() => getCategory3Columns(handleDownload), []);

  return (
    <div className="w-full">
      <Transition
        as={Fragment}
        show={isFilterOpen}
        enter="transition ease-in-out duration-200 transform"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition ease-in-out duration-200 transform"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div className="fixed right-0 inset-0 top-0 z-[500]">
          <aside
            ref={filterPanelRef}
            className="bg-white border-l-[1px] min-w-[24rem] overflow-y-auto max-w-[25rem] border-l-[#00000014] fixed px-8 py-5 right-0 h-screen"
          >
            <div className="flex items-center justify-between gap-3">
              <h3 className="whitespace-nowrap text-xl font-semibold text-[#49484C]">Fetch filters</h3>
              <div className="flex items-center gap-2">
                <Button className="border py-1 bg-[#7F56D9] text-white" onClick={handleResetFilters}>
                  Reset
                </Button>
                <Button className="border py-2 bg-[#7F56D9] text-white" onClick={() => setIsFilterOpen(false)}>
                  Close
                </Button>
              </div>
            </div>

            <div className="flex flex-col gap-4 pt-6">
              <div>
                <label className="text-sm font-medium text-[#49484C] mb-2 block">From Date - To Date</label>
                <DayPicker
                  classNames={DAY_PICKER_CLASSNAMES}
                  mode="range"
                  defaultMonth={draftRange?.from}
                  selected={draftRange}
                  onSelect={setDraftRange}
                  numberOfMonths={1}
                  disabled={{ after: moment(todayStr).toDate() }}
                />
              </div>

              <Button isLoading={isLoading} disabled={isLoading} className="text-white bg-[#586AF5]" onClick={handleApplyFilters}>
                Apply
              </Button>
            </div>
          </aside>
        </div>
      </Transition>

      <h2 className="text-center text-lg font-bold text-[#1B1A1F] pb-4">Stockist E-Invoice Bulk File Upload Details</h2>
      <div className="border-b border-[#E9E9E9] -mx-[3.75rem]" />

      <div className="flex items-center justify-end gap-3 py-6">
        <div className="flex items-center w-[16rem] rounded-md overflow-hidden border border-gray-300 bg-white">
          <Input
            placeholder="Search by PH Code"
            debounceTime={500}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value.trim())}
            containerClasses="w-full px-3 py-1 text-gray-700 border-none bg-white focus:outline-none focus:ring-0 focus:shadow-none"
          />
        </div>

        <div className="flex items-center gap-2 h-full relative">
          {hasActiveDateFilter && (
            <span className="absolute top-[-6px] right-[-6px] w-2.5 h-2.5 bg-red-500 rounded-full" />
          )}
          <Button className="bg-[#F1F1F1]" onClick={handleOpenFilters}>
            <img src={filterIcon} alt="filter-icon" />
          </Button>
        </div>
      </div>

      <Table columns={columns} data={data} isLoading={isLoading} showSearch={false} headClass="bg-[#F8F8F8]" />
    </div>
  );
};

export default Category3Files;
